import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const RFIActionModal = ({ rfi, onClose, onUpdate }) => {
  const [status, setStatus] = useState('');
  const [response, setResponse] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const { user } = useAuth();

  // Determine user's role
  const isInitiator = user?.id === rfi.raised_by;
  const isAssignee = user?.id === rfi.assigned_to;
  const isPendingMoreInfo = rfi.status === 'more_info_needed';

  // Can take action: assignee (when open) OR initiator (when more info needed)
  const canRespond = user?.role === 'admin' || (isAssignee && !isPendingMoreInfo);
  const canProvideInfo = isInitiator && isPendingMoreInfo;

  const handleFileSelect = (e) => {
    setSelectedFile(e.target.files[0]);
  };

  const uploadFile = async () => {
    if (!selectedFile) return null;

    setUploading(true);
    const fileData = new FormData();
    fileData.append('file', selectedFile);
    fileData.append('document_type', 'rfis');
    fileData.append('project_id', rfi.project_id);

    try {
      const resp = await axios.post(`${API}/upload`, fileData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return { url: resp.data.file_url, name: selectedFile.name };
    } catch (error) {
      toast.error('File upload failed');
      return null;
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      let fileData = null;
      
      if (selectedFile) {
        fileData = await uploadFile();
      }

      const params = {
        response,
      };

      if (fileData) {
        params.file_url = fileData.url;
        params.file_name = fileData.name;
      }

      if (canProvideInfo) {
        // Initiator providing more info
        params.action_type = 'resubmission';
      } else if (canRespond) {
        // Assignee responding
        params.status = status;
        params.action_type = 'response';
      }

      await axios.patch(`${API}/rfis/${rfi.id}`, null, { params });

      toast.success(canProvideInfo ? 'Information submitted successfully' : 'Response submitted successfully');
      onUpdate();
      onClose();
    } catch (error) {
      toast.error('Failed to update RFI');
    }
  };

  // Response history
  const responseHistory = rfi.response_history || [];
  const attachments = rfi.attachments || [];

  // Days overdue calculation
  const daysOverdue = () => {
    if (rfi.status === 'closed') return 0;
    const dueDate = new Date(rfi.due_date);
    const today = new Date();
    const diff = Math.floor((today - dueDate) / (1000 * 60 * 60 * 24));
    return diff > 0 ? diff : 0;
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="p-6 border-b border-slate-200">
          <h2 className="text-2xl font-bold text-slate-900">
            {canProvideInfo ? 'Provide Additional Information' : 'RFI Action'}
          </h2>
          <p className="text-sm text-slate-500 mt-1">{rfi.title}</p>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* RFI Info */}
          <div className="bg-slate-50 rounded-lg p-4">
            <p className="text-sm text-slate-600 mb-3">{rfi.description}</p>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-slate-500">Raised By:</span>
                <span className="ml-2 font-medium">{rfi.raised_by_name}</span>
              </div>
              <div>
                <span className="text-slate-500">Assigned To:</span>
                <span className="ml-2 font-medium">{rfi.assigned_to_name}</span>
              </div>
              <div>
                <span className="text-slate-500">Due Date:</span>
                <span className="ml-2 font-medium">{new Date(rfi.due_date).toLocaleDateString()}</span>
                {daysOverdue() > 0 && (
                  <span className="ml-2 text-red-600 text-xs font-semibold">({daysOverdue()} days overdue)</span>
                )}
              </div>
              <div>
                <span className="text-slate-500">Status:</span>
                <span className={`ml-2 px-2 py-0.5 rounded text-xs font-medium ${
                  rfi.status === 'closed' ? 'bg-green-100 text-green-700' :
                  rfi.status === 'more_info_needed' ? 'bg-amber-100 text-amber-700' :
                  'bg-blue-100 text-blue-700'
                }`}>
                  {rfi.status?.replace('_', ' ')}
                </span>
              </div>
              {rfi.specification_section && (
                <div>
                  <span className="text-slate-500">Spec Section:</span>
                  <span className="ml-2 font-medium">{rfi.specification_section}</span>
                </div>
              )}
              {rfi.drawing_reference && (
                <div>
                  <span className="text-slate-500">Drawing Ref:</span>
                  <span className="ml-2 font-medium">{rfi.drawing_reference}</span>
                </div>
              )}
            </div>
            
            {/* Show warning when more info needed */}
            {isPendingMoreInfo && (
              <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800">
                <strong>Awaiting Response:</strong> This RFI requires additional information from {rfi.raised_by_name}.
              </div>
            )}
          </div>

          {/* Response History */}
          {responseHistory.length > 0 && (
            <div className="border border-slate-200 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-slate-900 mb-3">Response History</h3>
              <div className="space-y-3 max-h-60 overflow-y-auto">
                {responseHistory.slice().reverse().map((entry, idx) => (
                  <div key={idx} className={`p-3 rounded-lg border-l-4 ${
                    entry.response_type === 'resubmission' ? 'bg-blue-50 border-l-blue-400' :
                    entry.status === 'closed' ? 'bg-green-50 border-l-green-400' :
                    entry.status === 'more_info_needed' ? 'bg-amber-50 border-l-amber-400' :
                    'bg-slate-50 border-l-slate-300'
                  }`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-900">{entry.user_name}</span>
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          entry.response_type === 'resubmission' ? 'bg-blue-100 text-blue-700' :
                          entry.status === 'closed' ? 'bg-green-100 text-green-700' :
                          entry.status === 'more_info_needed' ? 'bg-amber-100 text-amber-700' :
                          'bg-slate-100 text-slate-600'
                        }`}>
                          {entry.response_type === 'resubmission' ? 'Info Provided' : 
                           entry.status === 'more_info_needed' ? 'More Info Needed' :
                           entry.status || entry.response_type}
                        </span>
                      </div>
                      <span className="text-xs text-slate-500">
                        {new Date(entry.created_at).toLocaleString()}
                      </span>
                    </div>
                    {entry.comments && (
                      <p className="text-sm text-slate-600">{entry.comments}</p>
                    )}
                    {entry.file_url && (
                      <a 
                        href={entry.file_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 mt-2 text-sm text-blue-600 hover:underline"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                        </svg>
                        {entry.file_name || 'Attachment'}
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Attachments */}
          {attachments.length > 0 && (
            <div className="border border-slate-200 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-slate-900 mb-3">All Attachments</h3>
              <div className="grid grid-cols-2 gap-2">
                {attachments.map((att, idx) => (
                  <a 
                    key={idx}
                    href={att.file_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    download
                    className="flex items-center gap-2 p-2 bg-slate-50 rounded hover:bg-slate-100 transition-colors"
                  >
                    <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-900 truncate">{att.file_name}</div>
                      <div className="text-xs text-slate-500">by {att.uploaded_by}</div>
                    </div>
                    <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Action Form */}
          {(canRespond || canProvideInfo) && rfi.status !== 'closed' && (
            <form onSubmit={handleSubmit} className="space-y-4 border-t border-slate-200 pt-6">
              <h3 className="text-sm font-semibold text-slate-900">
                {canProvideInfo ? 'Provide Additional Information' : 'Submit Response'}
              </h3>
              
              {canRespond && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Status *</label>
                  <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-md"
                    required
                  >
                    <option value="">Select a status</option>
                    <option value="open">Open (Continue Discussion)</option>
                    <option value="more_info_needed">More Information Needed</option>
                    <option value="closed">Closed</option>
                  </select>
                  {status === 'more_info_needed' && (
                    <p className="text-xs text-amber-600 mt-1">
                      This will reassign the RFI back to the initiator ({rfi.raised_by_name}) for additional information.
                    </p>
                  )}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  {canProvideInfo ? 'Additional Information *' : 'Response *'}
                </label>
                <textarea
                  value={response}
                  onChange={(e) => setResponse(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-md"
                  rows="4"
                  placeholder={canProvideInfo ? 'Provide the requested additional information...' : 'Provide your response to this RFI...'}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Attach File</label>
                <input
                  type="file"
                  onChange={handleFileSelect}
                  className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm"
                />
                {selectedFile && (
                  <p className="text-xs text-slate-600 mt-1">Selected: {selectedFile.name}</p>
                )}
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  disabled={uploading || (canRespond && !status)}
                  className={`flex-1 text-white py-2 rounded-md disabled:opacity-50 ${
                    canProvideInfo ? 'bg-blue-600 hover:bg-blue-700' : 'bg-slate-900 hover:bg-slate-800'
                  }`}
                >
                  {uploading ? 'Uploading...' : canProvideInfo ? 'Submit Information' : 'Submit Response'}
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 border border-slate-200 py-2 rounded-md hover:bg-slate-50"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}

          {/* Read-only mode message */}
          {!canRespond && !canProvideInfo && rfi.status !== 'closed' && (
            <div className="text-center py-4 text-slate-500 border-t border-slate-200">
              {isPendingMoreInfo 
                ? 'Waiting for the initiator to provide additional information.'
                : 'You cannot take action on this RFI at this time.'}
            </div>
          )}

          {rfi.status === 'closed' && (
            <div className="text-center py-4 text-green-600 border-t border-slate-200 font-medium">
              This RFI has been closed.
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-200 bg-slate-50">
          <button
            onClick={onClose}
            className="w-full border border-slate-200 py-2 rounded-md hover:bg-white transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
